import React, { useState, useEffect } from 'react';
import {
  Network, Send, MessageSquare, Phone, Activity, ShieldCheck, 
  Plus, Settings, MoreVertical, CheckCircle2, AlertCircle, 
  RefreshCw, Search, Filter, ArrowUpRight, ArrowDownRight,
  Database, Link2, Trash2, Edit3, Globe, Lock, BarChart3, TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import API from '../api/api01';
import { toast } from 'sonner';
import { cn } from "../ui/utils";
import GatewayConfigModal from './GatewayConfigModal';

export default function GatewayManager() {
  const [providers, setProviders] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({
    sms_delivery: '0%',
    whatsapp_sent: '0',
    voice_connect: '0%',
    overall_success: '0%',
    recent_volume: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'providers' | 'logs' | 'analytics'>('providers');
  const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);
  const [newProvider, setNewProvider] = useState({ name: '', slug: '', is_active: true });

  // Config Modal State
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<{id: string, slug: string} | null>(null);

  const [confirmData, setConfirmData] = useState<{ id: string, status: boolean, name: string } | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
 
  const fetchData = async () => {
    try {
      setLoading(true);
      const [provRes, logRes, statsRes] = await Promise.all([
        API.get('/gateway/providers/'),
        API.get('/gateway/logs/'),
        API.get('/gateway/logs/stats/')
      ]);
      setProviders(provRes.data || []);
      setLogs(Array.isArray(logRes.data) ? logRes.data : (logRes.data?.results || []));
      setStats(statsRes.data || stats);
    } catch (err) {
      console.error("Gateway fetch error:", err);
      toast.error("Failed to sync gateway data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddProvider = async () => {
    try {
      if (!newProvider.name || !newProvider.slug) {
        toast.error("Name and Slug are required");
        return;
      }
      await API.post('/gateway/providers/', newProvider);
      toast.success("Provider added successfully");
      setIsAddProviderOpen(false);
      setNewProvider({ name: '', slug: '', is_active: true });
      fetchData();
    } catch (err) {
      toast.error("Failed to add provider");
    }
  };
  const initiateToggle = (id: string, currentStatus: boolean, name: string) => {
    if (currentStatus) {
      setConfirmData({ id, status: currentStatus, name });
      setIsConfirmOpen(true);
    } else {
      executeToggle(id, currentStatus);
    }
  };

  const executeToggle = async (id: string, currentStatus: boolean) => {
    try {
      await API.patch(`/gateway/providers/${id}/`, { is_active: !currentStatus });
      toast.success(`Provider ${!currentStatus ? 'activated' : 'deactivated'}`);
      setIsConfirmOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleDeleteProvider = async (id: string) => {
    try {
      await API.delete(`/gateway/providers/${id}/`);
      toast.success("Provider purged successfully");
      setDeleteConfirmId(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete provider. Check for active dependencies.");
    }
  };

  const kpis = [
    { label: 'SMS Delivery', value: stats.sms_delivery, icon: <Send className="h-5 w-5" />, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'WhatsApp Sent', value: stats.whatsapp_sent, icon: <MessageSquare className="h-5 w-5" />, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Call Connect', value: stats.voice_connect, icon: <Phone className="h-5 w-5" />, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { label: 'Health Index', value: stats.overall_success, icon: <Activity className="h-5 w-5" />, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  ];

  if (loading && providers.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-background text-muted-foreground gap-4">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium animate-pulse tracking-widest uppercase">Initializing Secure Gateway...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Network className="h-6 w-6 text-primary" />
            Integration Gateway
          </h1>
          <p className="text-sm text-muted-foreground mt-1 text-slate-500 dark:text-slate-400">
            Manage and monitor your communication channels. Powered by <span className="text-primary font-bold">Nimbus</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData} className="rounded-xl h-10 w-10 p-0 border-border/50 hover:bg-accent">
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
          
          <Dialog open={isAddProviderOpen} onOpenChange={setIsAddProviderOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-primary shadow-lg shadow-primary/20 gap-2 font-bold px-5">
                <Plus className="h-4 w-4" />
                Add Provider
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle>Add New Communication Provider</DialogTitle>
                <DialogDescription>
                  Register a new API provider to handle your SMS, WhatsApp, or Voice traffic.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Display Name</Label>
                  <Input id="name" placeholder="e.g. Nimbus IT" value={newProvider.name} onChange={e => setNewProvider({...newProvider, name: e.target.value})} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="slug">Slug (Identifier)</Label>
                  <Input id="slug" placeholder="e.g. nimbus" value={newProvider.slug} onChange={e => setNewProvider({...newProvider, slug: e.target.value.toLowerCase()})} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddProviderOpen(false)}>Cancel</Button>
                <Button onClick={handleAddProvider}>Save Provider</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Deactivation Confirmation Dialog */}
          <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
            <DialogContent className="rounded-3xl sm:max-w-[400px] p-0 overflow-hidden border-none shadow-2xl">
              <div className="bg-red-500 h-1.5 w-full" />
              <div className="p-6 pt-8 text-center">
                <div className="h-16 w-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <DialogHeader>
                  <DialogTitle className="text-xl font-black tracking-tight text-center">Deactivate Provider?</DialogTitle>
                  <DialogDescription className="text-center pt-2 text-slate-500 font-medium">
                    You are about to deactivate <span className="font-bold text-slate-900">{confirmData?.name}</span>. This will stop all outgoing traffic for this gateway.
                  </DialogDescription>
                </DialogHeader>
              </div>
              <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col sm:flex-row gap-2">
                <Button variant="ghost" onClick={() => setIsConfirmOpen(false)} className="rounded-xl flex-1 font-bold">
                  Keep Active
                </Button>
                <Button 
                  onClick={() => confirmData && executeToggle(confirmData.id, confirmData.status)} 
                  className="rounded-xl flex-1 bg-red-600 hover:bg-red-700 text-white font-black shadow-lg shadow-red-600/20"
                >
                  Confirm Deactivation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Provider Confirmation Dialog */}
          <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
            <DialogContent className="rounded-3xl sm:max-w-[400px] p-0 overflow-hidden border-none shadow-2xl">
              <div className="bg-slate-900 h-1.5 w-full" />
              <div className="p-6 pt-8 text-center">
                <div className="h-16 w-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="h-8 w-8 text-slate-900" />
                </div>
                <DialogHeader>
                  <DialogTitle className="text-xl font-black tracking-tight text-center">Permanent Deletion</DialogTitle>
                  <DialogDescription className="text-center pt-2 text-slate-500 font-medium">
                    Are you sure you want to completely remove this provider? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
              </div>
              <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col sm:flex-row gap-2">
                <Button variant="ghost" onClick={() => setDeleteConfirmId(null)} className="rounded-xl flex-1 font-bold">
                  Cancel
                </Button>
                <Button 
                  onClick={() => deleteConfirmId && handleDeleteProvider(deleteConfirmId)} 
                  className="rounded-xl flex-1 bg-slate-900 hover:bg-slate-800 text-white font-black shadow-lg shadow-slate-900/20"
                >
                  Delete Permanently
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="glass-card overflow-hidden border-none shadow-sm bg-slate-50 dark:bg-slate-900/40 rounded-2xl group hover:scale-[1.02] transition-all">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className={cn("p-2.5 rounded-xl transition-colors", kpi.bg, kpi.color)}>
                  {kpi.icon}
                </div>
                <Badge variant="outline" className="border-none text-[10px] font-bold text-slate-400">
                  REAL-TIME
                </Badge>
              </div>
              <div className="mt-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
                <h3 className="text-xl font-bold mt-0.5 tracking-tight">{kpi.value}</h3>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Tabs */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900/60 p-1.5 rounded-xl w-fit border border-border/20 backdrop-blur-sm">
          <button 
            onClick={() => setActiveTab('providers')}
            className={cn("px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all", activeTab === 'providers' ? "bg-white dark:bg-slate-800 shadow-md text-primary" : "text-muted-foreground hover:text-foreground")}
          >
            Connected Gateways
          </button>
          <button 
            onClick={() => setActiveTab('analytics')}
            className={cn("px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all", activeTab === 'analytics' ? "bg-white dark:bg-slate-800 shadow-md text-primary" : "text-muted-foreground hover:text-foreground")}
          >
            Health & Analytics
          </button>
          <button 
            onClick={() => setActiveTab('logs')}
            className={cn("px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all", activeTab === 'logs' ? "bg-white dark:bg-slate-800 shadow-md text-primary" : "text-muted-foreground hover:text-foreground")}
          >
            Live Traffic
          </button>
        </div>

        {activeTab === 'providers' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(providers || []).map((p) => (
              <Card key={p.id} className="group hover:shadow-xl transition-all duration-300 border-border/50 rounded-2xl overflow-hidden bg-card/50 backdrop-blur-sm">
                <div className={cn("h-1.5 transition-all duration-500", p.is_active ? "bg-primary/20 group-hover:bg-primary" : "bg-slate-300 group-hover:bg-slate-400")} />
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Database className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-bold tracking-tight">{p.name}</CardTitle>
                        <CardDescription className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {p.slug}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant={p.is_active ? 'default' : 'secondary'} className="rounded-lg px-2 py-0 h-5 text-[9px] font-black uppercase tracking-widest">
                      {p.is_active ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 pt-2">
                  <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                    {p.channels && p.channels.length > 0 ? p.channels.map((c: string) => (
                      <Badge key={c} variant="outline" className="text-[9px] font-bold py-0 bg-accent/30 border-none uppercase tracking-tighter">{c}</Badge>
                    )) : (
                      <span className="text-[10px] text-muted-foreground italic">No channels configured</span>
                    )}
                  </div>
                  <div className="space-y-2 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-border/30">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-1.5">
                        <Activity className="h-3 w-3 text-primary" /> API Monitor
                      </p>
                      <div className="flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-[8px] font-black text-emerald-500 uppercase tracking-tighter">Live</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                       <div className="flex items-center justify-between text-[10px]">
                          <span className="text-slate-500 font-medium italic">Endpoint</span>
                          <span className="text-slate-700 dark:text-slate-300 font-mono font-bold truncate max-w-[120px]">
                            {p.slug?.toUpperCase()}.API
                          </span>
                       </div>
                       <div className="flex items-center justify-between text-[10px]">
                          <span className="text-slate-500 font-medium italic">Latency</span>
                          <span className="text-emerald-600 font-bold">124ms</span>
                       </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border/30">
                    <div className="text-left">
                      <p className="text-[9px] uppercase font-black text-muted-foreground tracking-widest">Efficiency</p>
                      <p className={cn(
                        "text-sm font-black transition-colors",
                        p.success_rate === "N/A" ? "text-slate-400" : 
                        parseFloat(p.success_rate) >= 90 ? "text-emerald-500" : 
                        parseFloat(p.success_rate) >= 50 ? "text-orange-500" : "text-red-500"
                      )}>
                        {p.success_rate || "N/A"}
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 rounded-xl hover:bg-primary/10 text-primary"
                        onClick={() => {
                          setSelectedProvider({ id: p.id, slug: p.slug });
                          setIsConfigModalOpen(true);
                        }}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className={cn("h-8 w-8 rounded-xl transition-colors", p.is_active ? "hover:bg-red-500/10 text-red-500" : "hover:bg-emerald-500/10 text-emerald-500")}
                        onClick={() => initiateToggle(p.id, p.is_active, p.name)}
                      >
                        <ShieldCheck className="h-4 w-4" />
                      </Button>

                      {/* Delete Button - Only shown when disabled */}
                      {!p.is_active && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 rounded-xl hover:bg-slate-900 hover:text-white transition-all text-slate-400"
                          onClick={() => setDeleteConfirmId(p.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Card 
              onClick={() => setIsAddProviderOpen(true)}
              className="border-dashed border-2 border-border/50 hover:bg-accent/50 hover:border-primary/50 cursor-pointer transition-all duration-300 flex flex-col items-center justify-center min-h-[180px] gap-3 text-muted-foreground rounded-2xl group shadow-sm"
            >
              <div className="h-12 w-12 rounded-2xl bg-accent group-hover:bg-primary group-hover:text-white flex items-center justify-center transition-all">
                <Plus className="h-6 w-6" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest">Connect New Gateway</p>
            </Card>
          </div>
        ) : activeTab === 'analytics' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
             {/* Traffic Trend Chart */}
             <Card className="lg:col-span-2 rounded-2xl border-none shadow-sm bg-card/50 backdrop-blur-sm overflow-hidden">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-black tracking-tight flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        Traffic Velocity
                      </CardTitle>
                      <CardDescription>Real-time message volume across all channels (last 24h)</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.time_series || []}>
                        <defs>
                          <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="time" 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{fontSize: 10, fontWeight: 700}}
                          interval={3}
                        />
                        <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700}} />
                        <Tooltip 
                          contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                        />
                        <Area type="monotone" dataKey="volume" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorVolume)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
             </Card>

             {/* Provider Success Distribution */}
             <Card className="rounded-2xl border-none shadow-sm bg-card/50 backdrop-blur-sm overflow-hidden">
                <CardHeader>
                   <CardTitle className="text-lg font-black tracking-tight flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-emerald-500" />
                      Success Index
                   </CardTitle>
                   <CardDescription>Provider reliability scores</CardDescription>
                </CardHeader>
                <CardContent>
                   <div className="space-y-4">
                      {(stats.providers || []).map((p: any) => {
                        const rate = (p.success / p.total * 100) || 0;
                        return (
                          <div key={p.provider__slug} className="space-y-1.5">
                             <div className="flex justify-between items-center text-[11px] font-bold">
                                <span className="uppercase tracking-wider">{p.provider__name}</span>
                                <span className={cn(rate >= 90 ? "text-emerald-500" : "text-orange-500")}>{rate.toFixed(1)}%</span>
                             </div>
                             <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                  className={cn("h-full transition-all duration-1000", rate >= 90 ? "bg-emerald-500" : "bg-orange-500")}
                                  style={{ width: `${rate}%` }}
                                />
                             </div>
                             <div className="flex justify-between text-[9px] text-muted-foreground font-medium uppercase tracking-tighter">
                                <span>{p.total} Requests</span>
                                <span>{p.failed} Failures</span>
                             </div>
                          </div>
                        );
                      })}
                   </div>
                </CardContent>
             </Card>

             {/* Channel Breakdown */}
             <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(stats.channels || {}).map(([key, data]: [string, any]) => (
                  <Card key={key} className="rounded-2xl border-none shadow-sm bg-card/50 backdrop-blur-sm p-6 flex items-center gap-4 group hover:bg-white dark:hover:bg-slate-800 transition-all">
                     <div className={cn(
                       "h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner",
                       key === 'sms' ? "bg-blue-500/10 text-blue-500" : 
                       key === 'whatsapp' ? "bg-emerald-500/10 text-emerald-500" : "bg-orange-500/10 text-orange-500"
                     )}>
                        {key === 'sms' ? <Send size={24} /> : key === 'whatsapp' ? <MessageSquare size={24} /> : <Phone size={24} />}
                     </div>
                     <div>
                        <p className="text-[10px] font-black uppercase text-muted-foreground tracking-[0.2em]">{key} Channel</p>
                        <h4 className="text-xl font-black tracking-tight">{data.sent.toLocaleString()} <span className="text-[10px] text-slate-400 font-normal">Sent</span></h4>
                        <div className="flex items-center gap-1.5 mt-1">
                           <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                           <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-tighter">{data.rate} Efficiency</span>
                        </div>
                     </div>
                  </Card>
                ))}
             </div>
          </div>
        ) : (
          <Card className="rounded-2xl border border-border/50 shadow-sm overflow-hidden bg-card/50 backdrop-blur-sm">
            <div className="p-4 border-b border-border/20 bg-slate-50/50 dark:bg-slate-900/40 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 max-w-sm w-full bg-background rounded-xl px-3 border border-border/50 h-10 shadow-inner">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input placeholder="Search records by identifier..." className="bg-transparent border-none text-[11px] w-full focus:outline-none font-medium" />
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-[10px] font-bold px-3 py-1">
                  Last 500 Records
                </Badge>
                <Button variant="outline" size="sm" className="rounded-xl gap-2 text-[10px] font-bold uppercase tracking-widest border-border/50">
                  <Filter className="h-3.5 w-3.5" />
                  Filter
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent bg-slate-50/50 dark:bg-slate-900/20 border-b-border/20">
                    <TableHead className="text-[10px] font-black uppercase tracking-widest h-12 px-6">Timestamp</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest h-12">Channel</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest h-12">Provider</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest h-12">Recipient</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest h-12">Status</TableHead>
                    <TableHead className="text-right text-[10px] font-black uppercase tracking-widest h-12 px-6">Payload</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs && logs.length > 0 ? logs.map((log) => (
                    <TableRow key={log.id} className="group hover:bg-accent/30 transition-colors border-b-border/10">
                      <TableCell className="text-[11px] font-medium px-6 py-4 whitespace-nowrap">
                        {log.created_at ? new Date(log.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          "text-[9px] font-black py-0 px-2 uppercase tracking-tighter border-none",
                          log.channel === 'sms' ? "bg-blue-500/10 text-blue-600" : 
                          log.channel === 'whatsapp' ? "bg-emerald-500/10 text-emerald-600" : "bg-orange-500/10 text-orange-600"
                        )}>
                          {log.channel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[11px] font-bold uppercase tracking-tighter text-slate-400">
                        {log.provider_name || 'System'}
                      </TableCell>
                      <TableCell className="text-[11px] font-mono tracking-tight text-slate-500">
                        {log.identifier}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "text-[9px] font-black py-0 px-2 border-none uppercase tracking-widest", 
                          log.status === 'sent' ? "bg-emerald-500 text-white shadow-sm shadow-emerald-500/20" : 
                          log.status === 'failed' ? "bg-red-500 text-white shadow-sm shadow-red-500/20" : "bg-slate-500/10 text-slate-600"
                        )}>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right px-6">
                         <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl hover:bg-accent"><Link2 className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64 p-3 rounded-xl">
                               <div className="space-y-2">
                                  <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Raw Payload</p>
                                  <div className="bg-slate-50 dark:bg-slate-900 p-2 rounded-lg border border-border/50 max-h-32 overflow-y-auto">
                                     <pre className="text-[10px] font-mono whitespace-pre-wrap">{log.payload || 'No data'}</pre>
                                  </div>
                                  {log.error_message && (
                                    <>
                                      <p className="text-[10px] font-black uppercase text-red-500 tracking-widest mt-2">Error</p>
                                      <p className="text-[10px] text-red-600">{log.error_message}</p>
                                    </>
                                  )}
                               </div>
                            </DropdownMenuContent>
                         </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-24 text-muted-foreground">
                        <div className="flex flex-col items-center gap-3">
                          <div className="h-16 w-16 rounded-full bg-accent/20 flex items-center justify-center">
                            <Activity className="h-8 w-8 opacity-20" />
                          </div>
                          <p className="text-xs font-bold uppercase tracking-[0.2em]">No recent traffic detected</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <GatewayConfigModal 
        open={isConfigModalOpen}
        onOpenChange={setIsConfigModalOpen}
        onSuccess={fetchData}
        providerId={selectedProvider?.id}
        providerSlug={selectedProvider?.slug}
      />
    </div>
  );
}
